<template>
  <vs-popup title="Status Distribuição" :active.sync="isActive" @close="close()">
    <div class="d-flex flex-column" style="gap:16px; padding:10px;">
      <p class="step-description">Selecione o período para analisar a distribuição de atendimentos.</p>

      <div class="d-flex" style="align-items:center; gap:16px;">
        <div style="flex:1">
          <flat-pickr
            :config="datetimepickr"
            v-model="startAt"
            name="Período (de)"
            placeholder="Data e hora inicial"
            v-validate="'required'"
            style="width:100%; border-radius:6px; padding:8px 10px;"
          />
        </div>

        <div style="flex:1">
          <flat-pickr
            :config="finishAtConfig(datetimepickr)"
            v-model="finishAt"
            name="Período (até)"
            placeholder="Data e hora final"
            v-validate="'required'"
            :disabled="startAt === ''"
            style="width:100%; border-radius:6px; padding:8px 10px;"
          />
        </div>
      </div>

      <div v-if="loading" class="d-flex justify-center" style="padding: 20px;">
        <vs-progress indeterminate color="primary">Buscando dados...</vs-progress>
      </div>

      <div v-if="distributionDisabled && !loading" class="info-box">
        <p>{{ distributionDisabledMessage }}</p>
      </div>

      <div v-if="statusResult && statusResult.length > 0 && !loading && unifyConnectionsActive !== null" class="unify-badge">
        <span class="unify-label">Conexões Unificadas</span>
        <span class="unify-value" :class="unifyConnectionsActive ? 'is-on' : 'is-off'">
          {{ unifyConnectionsActive ? 'Ativo' : 'Inativo' }}
        </span>
      </div>

      <div v-if="statusResult && statusResult.length > 0 && !loading" class="status-result">
        <h4>Status da Distribuição</h4>
        <div class="status-table list1-table">
          <div class="status-header">
            <div class="status-col">Usuário</div>
            <div class="status-col">Está ativo?</div>
            <div class="status-col">Último Login</div>
            <div class="status-col">Último Logout</div>
            <div class="status-col">Atendimentos transferidos</div>
          </div>
          <div
            v-for="item in statusResult"
            :key="item.user_id"
            class="status-row"
          >
            <div class="status-col">{{ item.user_name }}</div>
            <div class="status-col">
              <span :class="item.is_active ? 'status-active' : 'status-inactive'">
                {{ item.is_active ? 'Sim' : 'Não' }}
              </span>
            </div>
            <div class="status-col">{{ item.last_login_at || '--/--/---- --:--:--' }}</div>
            <div class="status-col">{{ item.last_logout_at || '--/--/---- --:--:--' }}</div>
            <div class="status-col">{{ item.transfers_count }}</div>
          </div>
        </div>
      </div>

      <div v-if="statusResult && statusResult.length > 0 && !loading" class="status-result">
        <h4>Usuário, Departamento e Conexões</h4>
        <div class="status-table list2-table">
          <div class="status-header">
            <div class="status-col">Usuário</div>
            <div class="status-col">Departamento</div>
            <div class="status-col">Conexões</div>
          </div>
          <div
            v-for="item in statusResult"
            :key="`dept-${item.user_id}`"
            class="status-row"
          >
            <div class="status-col">{{ item.user_name }}</div>
            <div class="status-col">{{ item.user_department || 'Sem departamento' }}</div>
            <div class="status-col">
              <span v-if="item.instances && item.instances.length">
                {{ item.instances.join(', ') }}
              </span>
              <span v-else>Sem conexões</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="statusResult && statusResult.length > 0 && !loading" class="status-result">
        <h4>Momentos de Transferências Automáticas (sistema)</h4>
        <div class="status-table list3-table">
          <div class="status-header">
            <div class="status-col">Usuário</div>
            <div class="status-col">Momento</div>
            <div class="status-col">Protocolo</div>
          </div>
          <div
            v-for="group in groupedTransferDetails"
            :key="`group-${group.user_id}`"
            class="list3-group"
          >
            <div class="status-row list3-group-row" @click="toggleUserDetails(group.user_id)">
              <div class="status-col list3-user">
                <span class="toggle-icon">{{ isUserExpanded(group.user_id) ? 'v' : '>' }}</span>
                <span>{{ group.user_name }}</span>
                <span class="details-count">({{ group.details.length }})</span>
              </div>
              <div class="status-col">
                <span v-if="!isUserExpanded(group.user_id)">Clique para ver</span>
              </div>
              <div class="status-col"></div>
            </div>
            <div v-if="isUserExpanded(group.user_id)">
              <div
                v-for="(detail, index) in group.details"
                :key="`${group.user_id}-${index}`"
                class="status-row list3-detail-row"
              >
                <div class="status-col"></div>
                <div class="status-col">{{ detail.moment || 'Sem dados' }}</div>
                <div class="status-col">{{ detail.protocol || 'Sem dados' }}</div>
              </div>
              <div v-if="!group.details.length" class="status-row list3-detail-row">
                <div class="status-col"></div>
                <div class="status-col">Sem dados</div>
                <div class="status-col">Sem dados</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="statusResult && statusResult.length === 0 && !loading" class="no-data">
        <p>Nenhuma transferência automática encontrada no período selecionado.</p>
      </div>

      <div class="d-flex justify-end" v-if="!loading && !statusResult">
        <vs-button type="filled" @click="searchStatus" :disabled="disableSearch">
          Buscar
        </vs-button>
      </div>

      <div class="d-flex justify-center" v-if="statusResult && !loading" style="gap: 8px;">
        <vs-button type="border" @click="copySummary">
          Copiar
        </vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import {mapGetters} from 'vuex';
import 'flatpickr/dist/flatpickr.css';
import flatPickr from 'vue-flatpickr-component';
import {Portuguese} from 'flatpickr/dist/l10n/pt.js';

export default {
  name: 'DistributionStatus',
  components: {flatPickr},
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    tenantName: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters(['token']),
    disableSearch(){
      return !this.startAt || !this.finishAt || this.startAt === '' || this.finishAt === ''
    },
    unifyConnectionsActive() {
      if (!this.statusResult || this.statusResult.length === 0) return null;
      return this.statusResult[0].unify_connections_active === true;
    },
    groupedTransferDetails() {
      if (!this.statusResult || this.statusResult.length === 0) return [];
      return this.statusResult.map(item => ({
        user_id: item.user_id,
        user_name: item.user_name,
        details: item.transfers_details || []
      }));
    },
    transferDetailsRows() {
      if (!this.statusResult || this.statusResult.length === 0) return [];
      const rows = [];
      this.statusResult.forEach(item => {
        const details = item.transfers_details || [];
        if (details.length) {
          details.forEach((detail, index) => {
            rows.push({
              key: `${item.user_id}-${index}`,
              user_name: item.user_name,
              moment: detail.moment || null,
              protocol: detail.protocol || null
            });
          });
        } else {
          rows.push({
            key: `${item.user_id}-empty`,
            user_name: item.user_name,
            moment: null,
            protocol: null
          });
        }
      });
      return rows;
    }
  },
  data() {
    return {
      startAt: '',
      finishAt: '',
      statusResult: null,
      distributionDisabled: false,
      distributionDisabledMessage: '',
      expandedUsers: {},
      loading: false,
      datetimepickr: {
        enableTime: true,
        noCalendar: false,
        dateFormat: "Y-m-d H:i:S",
        time_24hr: true,
        locale: Portuguese
      }
    }
  },
  methods: {
    close() {
      this.startAt = '';
      this.finishAt = '';
      this.statusResult = null;
      this.loading = false;
      this.$emit('isActiveFalse');
    },
    finishAtConfig(config) {
      return {
        ...config,
        minDate: this.startAt
      }
    },
    clearResults() {
      this.statusResult = null;
      this.expandedUsers = {};
    },
    isUserExpanded(userId) {
      return !!this.expandedUsers[userId];
    },
    toggleUserDetails(userId) {
      this.expandedUsers = {
        ...this.expandedUsers,
        [userId]: !this.expandedUsers[userId]
      };
    },
    copySummary() {
      if (!this.statusResult || this.statusResult.length === 0) return;

      const lines = [];
      lines.push('=== STATUS DISTRIBUICAO ===');
      if (this.unifyConnectionsActive !== null) {
        lines.push(`Conexoes Unificadas ativo: ${this.unifyConnectionsActive ? 'Sim' : 'Nao'}`);
      }

      lines.push('');
      lines.push('LISTA 1 - STATUS');
      this.statusResult.forEach(item => {
        lines.push(
          `${item.user_name} | Ativo: ${item.is_active ? 'Sim' : 'Nao'} | Ultimo Login: ${item.last_login_at || '--/--/---- --:--:--'} | Ultimo Logout: ${item.last_logout_at || '--/--/---- --:--:--'} | Transferidos: ${item.transfers_count}`
        );
      });

      lines.push('');
      lines.push('LISTA 2 - DEPARTAMENTO E CONEXOES');
      this.statusResult.forEach(item => {
        const dept = item.user_department || 'Sem departamento';
        const instances = item.instances && item.instances.length ? item.instances.join(', ') : 'Sem conexoes';
        lines.push(`${item.user_name} | Departamento: ${dept} | Conexoes: ${instances}`);
      });

      lines.push('');
      lines.push('LISTA 3 - TRANSFERENCIAS');
      this.transferDetailsRows.forEach(row => {
        lines.push(`${row.user_name} | ${row.moment || 'Sem dados'} | Protocolo: ${row.protocol || 'Sem dados'}`);
      });

      const text = lines.join('\r\n');

      navigator.clipboard.writeText(text).then(() => {
        this.$vs.notify({
          color: 'success',
          title: 'Copiado!',
          text: 'Resumo copiado para a area de transferencia.'
        });
      }).catch(() => {
        this.$vs.notify({
          color: 'warning',
          title: 'Aviso',
          text: 'Nao foi possivel copiar automaticamente. Por favor, copie manualmente.'
        });
      });
    },
    searchStatus() {
      this.loading = true;
      this.statusResult = null;

      this.$store.dispatch('getDistributionStatus', {
        startAt: this.startAt,
        finishAt: this.finishAt,
        tenantName: this.tenantName,
        token: this.token
      }).then(response => {
        const data = response.data || [];

        if (data.length && data[0].relate_active === false) {
          this.statusResult = null;
          this.distributionDisabled = true;
          this.distributionDisabledMessage = data[0].notice || 'Distribuicao automatica desativada.';
          this.loading = false;

          return;
        }

        this.distributionDisabled = false;
        this.distributionDisabledMessage = '';
        this.statusResult = data;
        this.loading = false;
      }).catch(exception => {
        console.error(exception);
        this.loading = false;
        const message = (exception && exception.response && exception.response.data && (exception.response.data.message || (exception.response.data.error && exception.response.data.error.message))) || 'Ocorreu um erro ao buscar os dados! Entre em contato com o time técnico.';
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: message
        });
      });
    },
  },
  watch: {
    isActive(value) {
      if (!value) {
        this.startAt = '';
        this.finishAt = '';
        this.loading = false;
        this.statusResult = null;
        this.distributionDisabled = false;
        this.distributionDisabledMessage = '';
        this.expandedUsers = {};
      }
    },
    startAt() {
      this.statusResult = null;
      this.distributionDisabled = false;
      this.distributionDisabledMessage = '';
      this.expandedUsers = {};
    },
    finishAt() {
      this.statusResult = null;
      this.distributionDisabled = false;
      this.distributionDisabledMessage = '';
      this.expandedUsers = {};
    }
  }
}
</script>

<style lang="scss" scoped>
.d-flex {
  display: flex;
}

.flex-column {
  flex-direction: column;
}

.justify-end {
  justify-content: flex-end;
}

.justify-center {
  justify-content: center;
}

.step-description {
  font-size: 13px;
  color: #666;
  margin-bottom: 8px;
  line-height: 1.4;
}

.status-result {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;

  h4 {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 12px;
  }
}

.status-table {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.status-header,
.status-row {
  display: grid;
  gap: 8px;
  align-items: center;
}

.list1-table .status-header,
.list1-table .status-row {
  grid-template-columns: 2fr 1fr 1.6fr 1.6fr 1.2fr;
}

.list2-table .status-header,
.list2-table .status-row {
  grid-template-columns: 1.6fr 1.4fr 3fr;
}

.list3-table .status-header,
.list3-table .status-row {
  grid-template-columns: 1.6fr 1.6fr 1.6fr;
}

.list3-table .status-row {
  box-shadow: none;
  border-radius: 0;
}

.list3-group {
  border: 1px solid #e6e6e6;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.list3-group + .list3-group {
  margin-top: 8px;
}

.list3-group-row {
  cursor: pointer;
  background: #f7f8fc;
  font-weight: 600;
}

.list3-group-row:hover {
  background: #eef2ff;
}

.list3-user {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.toggle-icon {
  display: inline-block;
  width: 16px;
  text-align: center;
  font-weight: 700;
  color: #555;
}

.details-count {
  color: #777;
  font-size: 11px;
}

.list3-detail-row {
  background: #fff;
  border-top: 1px solid #f0f0f0;
}

.list3-detail-row .status-col:nth-child(2),
.list3-detail-row .status-col:nth-child(3) {
  padding-left: 10px;
}

.status-header {
  font-weight: 600;
  font-size: 12px;
  color: #555;
  padding-bottom: 8px;
  border-bottom: 1px solid #ddd;
}

.status-row {
  background: white;
  padding: 8px 10px;
  border-radius: 6px;
  font-size: 12px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.status-col {
  color: #333;
}

.status-active {
  color: #2e7d32;
  font-weight: 600;
}

.status-inactive {
  color: #c62828;
  font-weight: 600;
}

.unify-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #f1f5ff;
  border: 1px solid #cfe0ff;
  color: #20346b;
  font-size: 12px;
  font-weight: 600;
  margin: 4px 0;
}

.unify-label {
  letter-spacing: 0.2px;
}

.unify-value {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
}

.unify-value.is-on {
  background: #e6f6ea;
  color: #1e7b34;
}

.unify-value.is-off {
  background: #fdecea;
  color: #b23b2a;
}

.no-data {
  background: #f8f8f8;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  color: #666;
  font-size: 13px;
}

.info-box {
  background: #fff7e6;
  border: 1px solid #ffe0a3;
  color: #7a4b00;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  font-size: 13px;
}
</style>
